import React, { useMemo } from "react";
import { useTranslate } from "@refinedev/core";
import { VideoPositionPoint } from "types/";
import { AnalyticsLineChart, type DatasetConfig } from "./AnalyticsLineChart";
import { analyticsTheme } from "./analyticsTheme";
import {
  getPercentageAxis,
  getTimestampSeries,
  buildTimestampChartOptions,
  type ChapterMilestone,
} from "./utils/";

const T_PREFIX =
  "media.projects.analytics.dashboard.summaryCards.viewerRetention";

export interface ViewerRetentionChartProps {
  data: VideoPositionPoint[] | undefined;
  chapters?: ChapterMilestone[];
  maxSeconds?: number;
  loading: boolean;
}

export const ViewerRetentionChart: React.FC<ViewerRetentionChartProps> = ({
  data,
  chapters,
  maxSeconds,
  loading,
}) => {
  const t = useTranslate();

  const points = useMemo(() => data ?? [], [data]);

  const { xValues, labels } = useMemo(
    () => getTimestampSeries(points),
    [points]
  );

  const chartOptions = useMemo(
    () =>
      buildTimestampChartOptions({
        chapters,
        scales: { y: getPercentageAxis() },
        tooltipLabel: (ctx) =>
          t(`${T_PREFIX}.viewersAtTimestamp`, {
            value: Math.round((ctx.parsed.y ?? 0) * 100) / 100,
          }),
      }),
    [chapters, t]
  );

  const datasets = useMemo<DatasetConfig[]>(
    () => [
      {
        label: t(`${T_PREFIX}.viewers`),
        data: points.map((p) => Math.round(p.viewers_pct * 100) / 100),
        color: analyticsTheme.datasetBlue,
      },
    ],
    [points, t]
  );

  return (
    <AnalyticsLineChart
      title={t(`${T_PREFIX}.title`)}
      subtitle={t(`${T_PREFIX}.subtitle`)}
      labels={labels}
      datasets={datasets}
      xValues={xValues}
      xScaleType="linear"
      xMax={maxSeconds}
      loading={loading}
      height={200}
      chartOptions={chartOptions}
    />
  );
};
